import type { StripeSchoolSeatsResponse } from '@kimuno/api-sdk/lib/types/responses/Teacher';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../assets/styles/themes/tokens';

interface Props {
	seats: StripeSchoolSeatsResponse | undefined;
	loading: boolean;
}

const SeatOverviewHeader: React.FC<Props> = ({ seats, loading }) => {
	const { t } = useTranslation();

	if (loading || !seats) {
		return (
			<Paper elevation={0} sx={{ padding: 2, marginBottom: 2 }}>
				<Skeleton variant="text" width={220} height={32} />
				<Box display="flex" gap={4} marginTop={1}>
					<Skeleton variant="text" width={80} />
					<Skeleton variant="text" width={80} />
					<Skeleton variant="text" width={80} />
				</Box>
			</Paper>
		);
	}

	const packageLabel =
		seats.packageKey === 'kombi' ? t('Bibliothek & Fortbildungen') : t('Bibliothek');

	const noSeatsRemaining = seats.remaining <= 0;

	return (
		<Paper
			elevation={0}
			sx={{
				padding: 2,
				marginBottom: 2,
				border: `1px solid ${palette.action.disabledBackground}`,
			}}
			role="region"
			aria-label={t('Seat overview')}
		>
			<Typography variant="subtitle1" sx={{ color: palette.text.primary, fontWeight: 600 }}>
				{packageLabel}
			</Typography>
			<Box display="flex" gap={4} marginTop={1}>
				<Typography variant="body2" sx={{ color: palette.text.secondary }}>
					{t('Used')}: <strong>{seats.used}</strong>
				</Typography>
				<Typography variant="body2" sx={{ color: palette.text.secondary }}>
					{t('Licensed Seats')}: <strong>{seats.contracted}</strong>
				</Typography>
				<Typography
					variant="body2"
					sx={{
						color: noSeatsRemaining ? palette.error.main : palette.text.secondary,
						fontWeight: noSeatsRemaining ? 600 : 400,
					}}
				>
					{t('Remaining')}: <strong>{seats.remaining}</strong>
				</Typography>
			</Box>
			{noSeatsRemaining && (
				<Typography variant="body2" sx={{ color: palette.error.main, marginTop: 1 }} role="alert">
					{t('No seats remaining')}
				</Typography>
			)}
		</Paper>
	);
};

export default SeatOverviewHeader;
